import { Check } from "@phosphor-icons/react";
import { Img } from "@/components/media/Img";
import { cn } from "@/lib/cn";

export type AvatarSize = "xs" | "sm" | "md" | "lg" | "lg48" | "xl" | "2xl";

/** Sizes per DESIGN.md §5, plus the 48px store avatar. */
const SIZE_CLASSES: Record<AvatarSize, string> = {
  xs: "size-5",
  sm: "size-6",
  md: "size-8",
  lg: "size-10",
  lg48: "size-12",
  xl: "size-16",
  "2xl": "size-24",
};

const SIZE_PIXELS: Record<AvatarSize, string> = {
  xs: "20px",
  sm: "24px",
  md: "32px",
  lg: "40px",
  lg48: "48px",
  xl: "64px",
  "2xl": "96px",
};

const BADGE_SIZE_CLASSES: Record<AvatarSize, string> = {
  xs: "size-2.5",
  sm: "size-3",
  md: "size-3.5",
  lg: "size-4",
  lg48: "size-5",
  xl: "size-5",
  "2xl": "size-6",
};

export interface AvatarProps {
  size?: AvatarSize;
  alt?: string;
  verified?: boolean;
  online?: boolean;
  imageKey?: string;
  /** Load eagerly at high priority. Only for a page's primary avatar (the profile header), never in lists. */
  priority?: boolean;
  className?: string;
}

export function Avatar({
  size = "md",
  alt = "",
  verified = false,
  online = false,
  imageKey,
  priority = false,
  className,
}: AvatarProps) {
  return (
    <span className={cn("relative inline-flex shrink-0", SIZE_CLASSES[size], className)}>
      {imageKey ? (
        // Lazy unless asked: avatars repeat across cards and lists, so marking them all eager and high priority made
        // a dozen of them compete with a page's LCP image (the landing hero).
        <Img imageKey={imageKey} alt={alt} aspect="1/1" sizes={SIZE_PIXELS[size]} priority={priority} fill className="rounded-full" />
      ) : (
        <span
          className="size-full rounded-full bg-surface-3"
          role={alt ? "img" : undefined}
          aria-label={alt || undefined}
          aria-hidden={alt ? undefined : true}
        />
      )}
      {online && (
        <span
          className="absolute right-0 bottom-0 rounded-full bg-accent ring-2 ring-surface-1"
          style={{ width: "28%", height: "28%" }}
          aria-hidden="true"
        />
      )}
      {verified && (
        <span
          className={cn(
            "absolute right-0 bottom-0 flex items-center justify-center rounded-full bg-primary text-text-on-primary ring-2 ring-surface-1",
            BADGE_SIZE_CLASSES[size],
          )}
          role="img"
          aria-label="Verified"
        >
          <Check className="size-2/3" weight="bold" aria-hidden="true" />
        </span>
      )}
    </span>
  );
}
